import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FloatingField } from '@/components/ui/floating-field';
import { Combobox } from '@/components/ui/combobox';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/** Full IFTA member jurisdictions (48 contiguous US states + 10 Canadian provinces). */
const IFTA_JURISDICTIONS = [
  { value: 'AL', label: 'AL — Alabama' },
  { value: 'AR', label: 'AR — Arkansas' },
  { value: 'AZ', label: 'AZ — Arizona' },
  { value: 'CA', label: 'CA — California' },
  { value: 'CO', label: 'CO — Colorado' },
  { value: 'CT', label: 'CT — Connecticut' },
  { value: 'DE', label: 'DE — Delaware' },
  { value: 'FL', label: 'FL — Florida' },
  { value: 'GA', label: 'GA — Georgia' },
  { value: 'IA', label: 'IA — Iowa' },
  { value: 'ID', label: 'ID — Idaho' },
  { value: 'IL', label: 'IL — Illinois' },
  { value: 'IN', label: 'IN — Indiana' },
  { value: 'KS', label: 'KS — Kansas' },
  { value: 'KY', label: 'KY — Kentucky' },
  { value: 'LA', label: 'LA — Louisiana' },
  { value: 'MA', label: 'MA — Massachusetts' },
  { value: 'MD', label: 'MD — Maryland' },
  { value: 'ME', label: 'ME — Maine' },
  { value: 'MI', label: 'MI — Michigan' },
  { value: 'MN', label: 'MN — Minnesota' },
  { value: 'MO', label: 'MO — Missouri' },
  { value: 'MS', label: 'MS — Mississippi' },
  { value: 'MT', label: 'MT — Montana' },
  { value: 'NC', label: 'NC — North Carolina' },
  { value: 'ND', label: 'ND — North Dakota' },
  { value: 'NE', label: 'NE — Nebraska' },
  { value: 'NH', label: 'NH — New Hampshire' },
  { value: 'NJ', label: 'NJ — New Jersey' },
  { value: 'NM', label: 'NM — New Mexico' },
  { value: 'NV', label: 'NV — Nevada' },
  { value: 'NY', label: 'NY — New York' },
  { value: 'OH', label: 'OH — Ohio' },
  { value: 'OK', label: 'OK — Oklahoma' },
  { value: 'OR', label: 'OR — Oregon' },
  { value: 'PA', label: 'PA — Pennsylvania' },
  { value: 'RI', label: 'RI — Rhode Island' },
  { value: 'SC', label: 'SC — South Carolina' },
  { value: 'SD', label: 'SD — South Dakota' },
  { value: 'TN', label: 'TN — Tennessee' },
  { value: 'TX', label: 'TX — Texas' },
  { value: 'UT', label: 'UT — Utah' },
  { value: 'VA', label: 'VA — Virginia' },
  { value: 'VT', label: 'VT — Vermont' },
  { value: 'WA', label: 'WA — Washington' },
  { value: 'WI', label: 'WI — Wisconsin' },
  { value: 'WV', label: 'WV — West Virginia' },
  { value: 'WY', label: 'WY — Wyoming' },
  { value: 'AB', label: 'AB — Alberta' },
  { value: 'BC', label: 'BC — British Columbia' },
  { value: 'MB', label: 'MB — Manitoba' },
  { value: 'NB', label: 'NB — New Brunswick' },
  { value: 'NL', label: 'NL — Newfoundland and Labrador' },
  { value: 'NS', label: 'NS — Nova Scotia' },
  { value: 'ON', label: 'ON — Ontario' },
  { value: 'PE', label: 'PE — Prince Edward Island' },
  { value: 'QC', label: 'QC — Quebec' },
  { value: 'SK', label: 'SK — Saskatchewan' },
];

const emptyForm = (period = '2026-Q1') => ({
  state: '',
  period,
  miles: '',
  mpg: '6.5',
  taxPaid: '',
  gallons: '',
});

export default function IFTAPage() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [period, setPeriod] = useState('2026-Q1');
  const [form, setForm] = useState(() => emptyForm());

  const dataQ = useQuery({
    queryKey: ['ifta-page', period],
    queryFn: async () => {
      const [r, f] = await Promise.all([
        api('/api/v1/ifta'),
        api(`/api/v1/documents/fuel-rollup?period=${encodeURIComponent(period)}`),
      ]);
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load IFTA');
      if (f.status !== 200) throw new Error(f.data?.error ?? 'Failed to load fuel rollup');
      return {
        records: r.data.records ?? [],
        rollup: f.data.rows ?? [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load IFTA');
  }, [dataQ.isError, dataQ.error]);

  const records = dataQ.data?.records ?? [];
  const rollup = dataQ.data?.rollup ?? [];

  const handleAdd = async () => {
    if (!form.state || !form.period || !form.miles) {
      toast.error('State, period, and miles required');
      return;
    }
    const res = await api('/api/v1/ifta', {
      method: 'POST',
      body: {
        jurisdiction: form.state,
        period: form.period,
        taxableMiles: Number(form.miles),
        fuelTaxPaid: Number(form.taxPaid || 0),
        avgMpg: Number(form.mpg || 6.5),
        ...(form.gallons !== '' && form.gallons != null
          ? { fuelPurchasedGallons: Number(form.gallons) }
          : {}),
      },
    });
    if (res.status === 201) {
      toast.success('IFTA record saved');
      setForm(emptyForm(period));
      setShowAdd(false);
      if (res.data?.record) {
        qc.setQueryData(['ifta-page', period], (prev) => {
          if (!prev) return prev;
          const list = Array.isArray(prev.records) ? prev.records : [];
          return { ...prev, records: [res.data.record, ...list] };
        });
      } else {
        qc.invalidateQueries({ queryKey: ['ifta-page'] });
      }
    } else {
      toast.error(String(res.data?.error ?? 'Could not save IFTA record'));
    }
  };

  const applyRollup = (row) => {
    setForm({
      state: row.jurisdiction,
      period: row.period,
      miles: form.miles,
      mpg: form.mpg || '6.5',
      taxPaid: ((row.amountCents || 0) / 100).toFixed(2),
      gallons: row.gallons != null ? String(row.gallons) : '',
    });
    setShowAdd(true);
    toast.message(
      `Prefilled ${row.jurisdiction} ${row.period} from ${row.receiptCount} fuel receipt(s)`
    );
  };

  const iftaCols = [
    { key: 'jurisdiction', label: 'State', className: 'font-mono' },
    { key: 'period', label: 'Period' },
    {
      key: 'taxableMiles',
      label: 'Taxable Miles',
      render: (v) => <span className="font-mono">{(v || 0).toLocaleString()}</span>,
    },
    {
      key: 'fuelPurchasedGallons',
      label: 'Gallons',
      render: (v) => <span className="font-mono">{v != null ? Number(v).toFixed(2) : '—'}</span>,
    },
    {
      key: 'netTaxDue',
      label: 'Net Tax',
      render: (v) => (
        <span className="font-mono">{formatCurrency(Number(v) || 0)}</span>
      ),
    },
    {
      key: 'fuelTaxPaid',
      label: 'Tax Paid',
      render: (v) => <span className="font-mono">{formatCurrency(Number(v) || 0)}</span>,
    },
  ];

  const receiptCols = [
    { key: 'jurisdiction', label: 'State', className: 'font-mono' },
    { key: 'period', label: 'Period' },
    {
      key: 'gallons',
      label: 'Gallons',
      className: 'font-mono',
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      key: 'amountCents',
      label: 'Amount',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    { key: 'receiptCount', label: 'Count', className: 'font-mono' },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <Button size="sm" variant="outline" onClick={() => applyRollup(row)}>
          Prefill
        </Button>
      ),
    },
  ];

  const totalMiles = records.reduce((s, r) => s + (r.taxableMiles ?? 0), 0);
  const totalGallons =
    rollup.reduce((s, r) => s + (r.gallons ?? 0), 0) ||
    records.reduce((s, r) => s + (r.fuelPurchasedGallons ?? 0), 0);
  const totalReceipts = rollup.reduce((s, r) => s + (r.amountCents ?? 0), 0) / 100;

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="IFTA"
          description="International Fuel Tax Agreement reporting"
          icon="Fuel"
          metrics={[
            { label: 'Records', value: records.length },
            { label: 'Fuel rows', value: rollup.length },
          ]}
          actions={
            <Button onClick={() => setShowAdd(!showAdd)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Add Record
            </Button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Taxable Miles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{totalMiles.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Fuel Gallons
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{totalGallons.toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Receipts Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{formatCurrency(totalReceipts)}</p>
            </CardContent>
          </Card>
        </div>

        {showAdd && (
          <Card>
            <CardHeader>
              <CardTitle>New IFTA Record</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Jurisdiction *
                  </label>
                  <Combobox
                    options={IFTA_JURISDICTIONS}
                    value={form.state}
                    onChange={(v) => setForm({ ...form, state: v })}
                    placeholder="Search state or province…"
                    searchPlaceholder="Type to search…"
                    emptyMessage="No matching jurisdiction"
                    allowClear
                  />
                </div>
                <FloatingField
                  label="Period *"
                  value={form.period}
                  onChange={(e) => setForm({ ...form, period: e.target.value })}
                  placeholder="2026-Q1"
                />
                <FloatingField
                  label="Taxable Miles"
                  type="number"
                  value={form.miles}
                  onChange={(e) => setForm({ ...form, miles: e.target.value })}
                  required
                />
                <FloatingField
                  label="Avg MPG"
                  type="number"
                  step="0.1"
                  value={form.mpg}
                  onChange={(e) => setForm({ ...form, mpg: e.target.value })}
                />
                <FloatingField
                  label="Fuel Tax Paid ($)"
                  type="number"
                  step="0.01"
                  value={form.taxPaid}
                  onChange={(e) => setForm({ ...form, taxPaid: e.target.value })}
                />
                <FloatingField
                  label="Fuel Gallons"
                  type="number"
                  step="0.1"
                  value={form.gallons}
                  onChange={(e) => setForm({ ...form, gallons: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAdd}>Save Record</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Fuel rollup period:</label>
          <input
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setForm((f) => ({ ...f, period: e.target.value }));
            }}
            placeholder="2026-Q1"
            className="w-40 px-3 py-2 border rounded-md text-sm bg-background outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Records by Jurisdiction</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={iftaCols}
              data={records}
              emptyMessage={dataQ.isLoading ? 'Loading…' : 'No IFTA records'}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Fuel Receipts ({period})</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={receiptCols}
              data={rollup}
              emptyMessage={`No fuel receipts with jurisdiction/gallons for ${period}`}
            />
          </CardContent>
        </Card>
      </div>
);
}
